import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Check, Loader2, Users } from "lucide-react";
import { useContent } from "../content-context";
import { Eyebrow, Reveal } from "../components/Reveal";
import API from "../api";

const euro = (n) => `€${Number(n).toLocaleString("en-IE", { maximumFractionDigits: 0 })}`;

export default function ActivityBookingSection() {
  const content = useContent();
  const activities = ((content && content.activities) || []).filter((a) => a && a.active !== false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ date: "", guests: 2, name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const choose = (a) => {
    setSelected(a);
    setError("");
    setTimeout(() => document.getElementById("activity-booking-form")?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const perPerson = selected ? (selected.mode === "deposit" ? Number(selected.deposit) : Number(selected.price)) : 0;
  const total = selected ? (perPerson * (Number(form.guests) || 1)).toFixed(2) : "0.00";

  const book = async () => {
    if (!selected) {
      setError("Choose an activity first.");
      return;
    }
    if (!form.date) {
      setError("Please pick a date for your adventure.");
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError("Please add your name, email and phone.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/activities/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ activity_id: selected.id, booking_date: form.date, guests: Number(form.guests) || 1, name: form.name, email: form.email, phone: form.phone, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not start booking");
      toast.loading("Opening secure checkout…", { duration: 3500 });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message || "Could not start booking — please try again.");
      setBusy(false);
    }
  };

  if (!activities.length) return null;

  return (
    <section className="border-t border-gold/15 bg-navy py-20 md:py-28" data-testid="activity-booking-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <Reveal>
          <Eyebrow light>Book an activity</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-2xl font-display text-4xl font-medium leading-tight text-ivory md:text-5xl" data-testid="activity-booking-heading">
            Pick your adventure — book it in two minutes
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-ivory/80 md:text-base">
            Choose your activity and date, pay your deposit by card, and we'll confirm your start time
            by email. The balance is settled on the day.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {activities.map((a, i) => (
            <Reveal key={a.id} delay={0.06 * i}>
              <div
                id={`activity-${a.id}`}
                className={`flex h-full flex-col border p-6 transition-colors duration-300 ${selected && selected.id === a.id ? "border-gold bg-navy-2" : "border-ivory/15 bg-navy-2/60 hover:border-gold/50"}`}
                data-testid={`activity-card-${a.id}`}
              >
                <h3 className="font-display text-2xl text-ivory">{a.name}</h3>
                <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-ivory/70">{a.blurb}</p>
                <p className="mt-4 text-sm font-light text-gold-2">
                  {a.mode === "deposit"
                    ? `${euro(a.deposit)} per person deposit today · ${euro(a.price)} full price`
                    : `${euro(a.price)} per person`}
                </p>
                <button
                  onClick={() => choose(a)}
                  data-testid={`activity-book-${a.id}`}
                  className="mt-5 flex items-center justify-center gap-2 border border-gold/60 px-5 py-3 text-[11px] font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:bg-gold hover:text-navy"
                >
                  {selected && selected.id === a.id ? <Check className="h-3.5 w-3.5" /> : null}
                  {selected && selected.id === a.id ? "Chosen" : "Book this"}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <div id="activity-booking-form" className="mx-auto mt-14 max-w-2xl border border-ivory/15 bg-navy-2 p-8 md:p-10" data-testid="activity-booking-form-panel">
          <p className="font-display text-2xl text-ivory">
            {selected ? `Booking: ${selected.name}` : "Your booking details"}
          </p>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">
                <CalendarDays className="h-3.5 w-3.5 text-gold" /> Date
              </span>
              <input
                type="date"
                value={form.date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={set("date")}
                data-testid="activity-date"
                className="mt-2 w-full border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold"
              />
            </label>
            <label className="block">
              <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">
                <Users className="h-3.5 w-3.5 text-gold" /> Guests
              </span>
              <select
                value={form.guests}
                onChange={set("guests")}
                data-testid="activity-guests"
                className="mt-2 w-full border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold"
              >
                {Array.from({ length: 22 }, (_, n) => (
                  <option key={n + 1} value={n + 1} className="bg-navy text-ivory">
                    {n + 1} {n === 0 ? "guest" : "guests"}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Your name</span>
              <input value={form.name} onChange={set("name")} data-testid="activity-name" className="mt-2 w-full border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Email</span>
              <input value={form.email} onChange={set("email")} type="email" data-testid="activity-email" className="mt-2 w-full border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Phone / WhatsApp</span>
              <input value={form.phone} onChange={set("phone")} type="tel" data-testid="activity-phone" className="mt-2 w-full border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold" />
            </label>
          </div>

          {selected && (
            <p className="mt-6 text-sm font-light text-ivory/85" data-testid="activity-total">
              Paying today: <span className="text-gold-2">{euro(total)}</span>
              {selected.mode === "deposit" ? ` deposit (${euro(selected.deposit)} × ${form.guests})` : " — full price"}
            </p>
          )}
          {error && (
            <p className="mt-4 border border-red-400/40 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="activity-error">
              {error}
            </p>
          )}
          <button
            onClick={book}
            disabled={busy}
            data-testid="activity-book-button"
            className="mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
            {busy ? "Opening secure checkout…" : selected ? `Book & pay — ${euro(total)}` : "Choose an activity above"}
          </button>
          <p className="mt-4 text-center text-[11px] font-light text-ivory/50">
            Secure card payment by Stripe · We confirm your start time by email
          </p>
        </div>
      </div>
    </section>
  );
}
